import { useAccounting } from "@/contexts/AccountingContext";
import { useState } from "react";
import { Search, ChevronDown, ChevronRight, Download, Filter, Shield, Clock, Globe, History, ArrowRight } from "lucide-react";
import { AuditEntry, AuditFieldChange } from "@/lib/accounting/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AuditLogsPage = () => {
  const { auditLog } = useAccounting();
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [moduleFilter, setModuleFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>("all");

  const uniqueModules = [...new Set(auditLog.map((a) => a.module))];
  const uniqueUsers = [...new Set(auditLog.map((a) => a.userName))];

  const filtered = auditLog.filter((a) => {
    const matchesSearch =
      a.details.toLowerCase().includes(search.toLowerCase()) ||
      a.userName.toLowerCase().includes(search.toLowerCase()) ||
      a.module.toLowerCase().includes(search.toLowerCase()) ||
      a.entityRef.toLowerCase().includes(search.toLowerCase());
    const matchesAction = actionFilter === "all" || a.action === actionFilter;
    const matchesModule = moduleFilter === "all" || a.module === moduleFilter;
    const matchesUser = userFilter === "all" || a.userName === userFilter;
    return matchesSearch && matchesAction && matchesModule && matchesUser;
  });

  const actionColors: Record<string, string> = {
    Created: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    Updated: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    Approved: "bg-primary/10 text-primary border-primary/20",
    Rejected: "bg-destructive/10 text-destructive border-destructive/20",
    Cancelled: "bg-muted text-muted-foreground border-border",
    Deleted: "bg-destructive/10 text-destructive border-destructive/20",
    Viewed: "bg-secondary text-secondary-foreground border-border",
    Exported: "bg-violet-500/10 text-violet-600 border-violet-500/20",
    Login: "bg-sky-500/10 text-sky-600 border-sky-500/20",
    Logout: "bg-slate-500/10 text-slate-600 border-slate-500/20",
  };

  const actionIcons: Record<string, string> = {
    Created: "➕", Updated: "✏️", Approved: "✅", Rejected: "❌",
    Cancelled: "🚫", Deleted: "🗑️", Viewed: "👁️", Exported: "📤", Login: "🔑", Logout: "🚪",
  };

  const exportCSV = () => {
    const headers = ["Timestamp", "User", "Role", "Action", "Module", "Reference", "Details", "IP Address", "Version"];
    const rows = filtered.map((e) => [
      new Date(e.timestamp).toLocaleString("en-IN"),
      e.userName, e.userRole, e.action, e.module, e.entityRef, e.details, e.ipAddress, e.version ?? "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audit-trail-${new Date().toISOString().split("T")[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const stats = {
    total: auditLog.length,
    today: auditLog.filter((a) => a.timestamp.startsWith(new Date().toISOString().split("T")[0])).length,
    changes: auditLog.filter((a) => a.action === "Updated" || a.action === "Deleted").length,
    approvals: auditLog.filter((a) => a.action === "Approved" || a.action === "Rejected").length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Audit Trail
          </h1>
          <p className="text-sm text-muted-foreground">Complete history of all financial activities for compliance & transparency</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} className="gap-2">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Entries", value: stats.total, icon: History, color: "text-primary" },
          { label: "Today", value: stats.today, icon: Clock, color: "text-emerald-600" },
          { label: "Modifications", value: stats.changes, icon: Filter, color: "text-amber-600" },
          { label: "Reviews", value: stats.approvals, icon: Shield, color: "text-violet-600" },
        ].map((s) => (
          <div key={s.label} className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{s.label}</p>
              <s.icon className={`h-4 w-4 ${s.color}`} />
            </div>
            <p className="text-2xl font-bold text-foreground mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-2 flex-1 max-w-sm">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search audit logs..." className="bg-transparent text-sm outline-none w-full text-foreground placeholder:text-muted-foreground" />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[140px] h-9 text-xs"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {["Created", "Updated", "Deleted", "Approved", "Rejected", "Cancelled", "Login"].map((a) => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger className="w-[140px] h-9 text-xs"><SelectValue placeholder="Module" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modules</SelectItem>
            {uniqueModules.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="w-[140px] h-9 text-xs"><SelectValue placeholder="User" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {uniqueUsers.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Audit Table */}
      <div className="rounded-lg bg-card border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="w-8 py-3 px-2" />
                {["Timestamp", "User", "Action", "Module", "Reference", "IP Address", "Ver.", "Details"].map((h) => (
                  <th key={h} className="text-left py-3 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => (
                <AuditRow
                  key={entry.id}
                  entry={entry}
                  isExpanded={expandedId === entry.id}
                  onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                  actionColors={actionColors}
                  actionIcons={actionIcons}
                />
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="py-16 text-center text-muted-foreground">
                  <Shield className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No audit entries found</p>
                  <p className="text-xs mt-1">Try adjusting your filters</p>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Showing {filtered.length} of {auditLog.length} entries • All timestamps in IST • Audit data is immutable and tamper-proof
      </p>
    </div>
  );
};

function AuditRow({ entry, isExpanded, onToggle, actionColors, actionIcons }: {
  entry: AuditEntry; isExpanded: boolean; onToggle: () => void;
  actionColors: Record<string, string>; actionIcons: Record<string, string>;
}) {
  const hasDetails = entry.changes?.length || entry.previousData || entry.newData;
  return (
    <>
      <tr
        className={`border-t border-border/50 transition-colors cursor-pointer ${isExpanded ? "bg-primary/5" : "hover:bg-muted/30"}`}
        onClick={onToggle}
      >
        <td className="py-3 px-2 text-center">
          {hasDetails ? (
            isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ) : <span className="w-4 h-4 block" />}
        </td>
        <td className="py-3 px-3 text-muted-foreground text-xs whitespace-nowrap">
          <div>{new Date(entry.timestamp).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
          <div className="text-[10px] opacity-70">{new Date(entry.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
        </td>
        <td className="py-3 px-3">
          <div className="text-foreground font-medium text-xs">{entry.userName}</div>
          <div className="text-[10px] text-muted-foreground">{entry.userRole}</div>
        </td>
        <td className="py-3 px-3">
          <Badge variant="outline" className={`text-[10px] font-medium ${actionColors[entry.action] || "bg-muted text-muted-foreground"}`}>
            <span className="mr-1">{actionIcons[entry.action]}</span>{entry.action}
          </Badge>
        </td>
        <td className="py-3 px-3 text-foreground text-xs">{entry.module}</td>
        <td className="py-3 px-3 font-medium text-primary text-xs">{entry.entityRef}</td>
        <td className="py-3 px-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1"><Globe className="h-3 w-3" />{entry.ipAddress}</div>
        </td>
        <td className="py-3 px-3 text-xs text-muted-foreground">{entry.version ? `v${entry.version}` : "—"}</td>
        <td className="py-3 px-3 text-muted-foreground text-xs max-w-xs truncate">{entry.details}</td>
      </tr>
      {isExpanded && hasDetails && (
        <tr className="bg-muted/20">
          <td colSpan={9} className="px-6 py-4">
            <ChangeDetails entry={entry} />
          </td>
        </tr>
      )}
    </>
  );
}

function ChangeDetails({ entry }: { entry: AuditEntry }) {
  const changes = entry.changes;
  let prevObj: Record<string, unknown> | null = null;
  let newObj: Record<string, unknown> | null = null;
  try { if (entry.previousData) prevObj = JSON.parse(entry.previousData); } catch {}
  try { if (entry.newData) newObj = JSON.parse(entry.newData); } catch {}

  return (
    <div className="space-y-4">
      {/* Field-level changes */}
      {changes && changes.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
            <History className="h-3.5 w-3.5 text-primary" /> Field Changes
          </h4>
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left py-2 px-3 font-medium text-muted-foreground">Field</th>
                  <th className="text-left py-2 px-3 font-medium text-destructive/70">Old Value</th>
                  <th className="w-8 text-center py-2 px-1" />
                  <th className="text-left py-2 px-3 font-medium text-emerald-600">New Value</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c: AuditFieldChange, i: number) => (
                  <tr key={i} className="border-t border-border/50">
                    <td className="py-2 px-3 font-medium text-foreground capitalize">{c.field.replace(/([A-Z])/g, " $1").trim()}</td>
                    <td className="py-2 px-3">
                      <span className="bg-destructive/10 text-destructive px-1.5 py-0.5 rounded text-[11px] font-mono">{c.oldValue || "—"}</span>
                    </td>
                    <td className="text-center"><ArrowRight className="h-3 w-3 text-muted-foreground inline" /></td>
                    <td className="py-2 px-3">
                      <span className="bg-emerald-500/10 text-emerald-600 px-1.5 py-0.5 rounded text-[11px] font-mono">{c.newValue}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Raw data snapshots */}
      {(prevObj || newObj) && !changes?.length && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {prevObj && (
            <div>
              <h4 className="text-xs font-semibold text-destructive/70 mb-1.5">Previous State</h4>
              <pre className="bg-destructive/5 border border-destructive/10 rounded-md p-3 text-[11px] text-foreground font-mono overflow-auto max-h-40">
                {JSON.stringify(prevObj, null, 2)}
              </pre>
            </div>
          )}
          {newObj && (
            <div>
              <h4 className="text-xs font-semibold text-emerald-600 mb-1.5">{prevObj ? "New State" : "Created Data"}</h4>
              <pre className="bg-emerald-500/5 border border-emerald-500/10 rounded-md p-3 text-[11px] text-foreground font-mono overflow-auto max-h-40">
                {JSON.stringify(newObj, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Metadata */}
      <div className="flex flex-wrap gap-4 text-[10px] text-muted-foreground pt-2 border-t border-border/50">
        <span>Entry ID: <span className="font-mono text-foreground">{entry.id}</span></span>
        <span>Entity: <span className="font-mono text-foreground">{entry.entityId}</span></span>
        {entry.sessionId && <span>Session: <span className="font-mono text-foreground">{entry.sessionId}</span></span>}
        <span>IP: <span className="font-mono text-foreground">{entry.ipAddress}</span></span>
      </div>
    </div>
  );
}

export default AuditLogsPage;
